import { useState, useMemo, useCallback } from "react";
import { CalendarDays, ListFilter, ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useFollowUpQueue } from "@/api/followups/hooks";
import { ActiveWorkspace } from "@/components/followup/ActiveWorkspace";
import { getPriorityFromDate } from "@/components/followup/FollowUpStatusBadge";
import { FollowUpCalendarView } from "@/components/followup/FollowUpCalendarView";
import { useScrollMemory } from "@/hooks/useScrollMemory";

export default function FollowUpUserDashboard() {
  // follow_up_user: the backend forces assignedTo = currentUser._id
  const { data: queueResult, isLoading } = useFollowUpQueue({ limit: 200 });
  const sponsors = useMemo(() => queueResult?.data || [], [queueResult?.data]);

  const [activeTab, setActiveTab] = useState("queue");
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [workspaceBatch, setWorkspaceBatch] = useState([]);
  const [workspaceSponsor, setWorkspaceSponsor] = useState(null);
  const [workspaceOrigin, setWorkspaceOrigin] = useState(null);
  const [lastOpenedId, setLastOpenedId] = useState(null);

  const queueScroll = useScrollMemory();
  const calendarScroll = useScrollMemory();

  const overdueCount = useMemo(
    () =>
      sponsors.filter(
        (s) => getPriorityFromDate(s.followUp?.nextActionDate) === "overdue",
      ).length,
    [sponsors],
  );

  const openWorkspace = useCallback(
    (sponsorOrBatch, origin) => {
      queueScroll.save();
      calendarScroll.save();
      const batch = Array.isArray(sponsorOrBatch)
        ? sponsorOrBatch
        : [sponsorOrBatch];
      setWorkspaceBatch(batch);
      setWorkspaceSponsor(batch[0] ?? null);
      setWorkspaceOrigin(origin);
      setWorkspaceOpen(true);
    },
    [queueScroll, calendarScroll],
  );

  const closeWorkspace = useCallback(() => {
    setWorkspaceOpen(false);
    setWorkspaceSponsor(null);
    setWorkspaceBatch([]);
    requestAnimationFrame(() => {
      if (workspaceOrigin === "queue") queueScroll.restore();
      if (workspaceOrigin === "calendar") calendarScroll.restore();
    });
    if (workspaceOrigin === "calendar") setActiveTab("calendar");
  }, [workspaceOrigin, queueScroll, calendarScroll]);

  const handleLogSuccess = useCallback((updates) => {
    setWorkspaceBatch((prev) =>
      prev.map((sp) =>
        sp._id === updates._id
          ? { ...sp, followUp: { ...(sp.followUp || {}), ...updates.followUp } }
          : sp,
      ),
    );
  }, []);

  const handleCalendarPullToWorkspace = useCallback(
    (sponsor) => {
      openWorkspace(sponsor, "calendar");
    },
    [openWorkspace],
  );

  return (
    <div className="flex flex-col min-h-full">
      {/* ── Workspace Panel — always mounted ── */}
      <div className={cn(!workspaceOpen && "hidden")}>
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b -mx-3 md:-mx-6 px-3 md:px-6">
          <div className="pt-3 pb-2 flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={closeWorkspace}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {workspaceOrigin === "calendar" ? (
                <CalendarDays className="h-3.5 w-3.5" />
              ) : (
                <ListFilter className="h-3.5 w-3.5" />
              )}
              {workspaceOrigin === "calendar"
                ? "Back to Calendar"
                : "Back to Queue"}
            </Button>
            {workspaceBatch.length > 1 && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                {workspaceBatch.length} sponsors
              </span>
            )}
          </div>
        </div>
        <div className="pt-4">
          <ActiveWorkspace
            key={workspaceSponsor?._id ?? "empty"}
            sponsor={workspaceSponsor}
            batch={workspaceBatch}
            onSelectFromBatch={(sp) => setWorkspaceSponsor(sp)}
            onLogSuccess={handleLogSuccess}
          />
        </div>
      </div>

      {/* ── Sticky Header — hidden during workspace ── */}
      <div
        className={cn(
          "sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b -mx-3 md:-mx-6 px-3 md:px-6",
          workspaceOpen && "hidden",
        )}
      >
        <div className="pt-3 pb-2 flex items-start justify-between">
          <div>
            <h1 className="text-lg font-semibold leading-tight">My Queue</h1>
            <p className="text-xs text-muted-foreground">
              {sponsors.length} assigned · {overdueCount} overdue
            </p>
          </div>
        </div>

        {/* Sub-navigation */}
        <div className="flex items-center gap-1 pb-2">
          {[
            { id: "queue", label: "Queue", icon: ListFilter },
            { id: "calendar", label: "Calendar", icon: CalendarDays },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => !workspaceOpen && setActiveTab(id)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
                workspaceOpen
                  ? "cursor-not-allowed text-muted-foreground/40"
                  : activeTab === id
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Queue — always mounted ── */}
      <div
        ref={queueScroll.ref}
        className={cn(
          "pt-4 flex-1 overflow-y-auto",
          (workspaceOpen || activeTab !== "queue") && "hidden",
        )}
      >
        <ActiveWorkspace sponsors={sponsors} isLoading={isLoading} />
      </div>

      {/* ── Calendar — always mounted ── */}
      <div
        className={cn(
          "pt-4 pb-6",
          (workspaceOpen || activeTab !== "calendar") && "hidden",
        )}
      >
        <FollowUpCalendarView
          scrollRef={calendarScroll.ref}
          onPullToWorkspace={handleCalendarPullToWorkspace}
        />
      </div>
    </div>
  );
}
